import { initializeApp } from 'firebase/app';
import { connectAuthEmulator, getAuth, inMemoryPersistence, setPersistence } from 'firebase/auth';
import { connectFirestoreEmulator, getFirestore } from 'firebase/firestore';
import { connectStorageEmulator, getStorage } from 'firebase/storage';
import { initializeAppCheck, ReCaptchaV3Provider } from 'firebase/app-check';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

/**
 * Whether this build was given a real project to talk to.
 *
 * Firebase does not complain about a placeholder here. `initializeApp` accepts
 * anything, and the first symptom is a sign-in that fails with
 * `auth/api-key-not-valid` — which reads like a broken app rather than an
 * unfilled config file, and sends whoever is looking at it into the wrong half
 * of the system. Naming it early costs nothing and saves that hour.
 *
 * These values are inlined at build time, so this is a fact about the build, not
 * about the environment it is running in.
 */
export const configProblem: string | null = (() => {
  const missing = Object.entries(firebaseConfig)
    .filter(([, v]) => !v || v === 'REPLACE_ME')
    .map(([k]) => k);
  if (!missing.length) return null;
  return missing.length === Object.keys(firebaseConfig).length
    ? 'This build has no Firebase configuration at all.'
    : `This build is missing Firebase configuration: ${missing.join(', ')}.`;
})();

const app = initializeApp(firebaseConfig);

/**
 * App Check (§3) — abuse protection in front of Firestore and Storage.
 *
 * It attests that a request came from *this app*, which is the only defense
 * available against someone taking the public web config and hammering the
 * database directly with a script. Nothing else here can tell the difference:
 * security rules answer "may this signed-in person do this", not "is this the
 * app I published".
 *
 * Wired only when a site key is configured, so local development and the
 * emulator suite run without one and without a warning that trains people to
 * ignore warnings. Enforcement is switched on per-service in the Firebase
 * console, and it should be switched on *after* a build carrying this key has
 * been deployed and observed — turning it on first locks out the app that is
 * already live.
 */
const siteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY;
if (siteKey) {
  // A debug token lets a developer run a real (non-emulator) project locally.
  // It is registered in the console per machine and is not a secret in code.
  const debugToken = import.meta.env.VITE_APPCHECK_DEBUG_TOKEN;
  if (debugToken) {
    (globalThis as unknown as Record<string, unknown>).FIREBASE_APPCHECK_DEBUG_TOKEN = debugToken;
  }
  initializeAppCheck(app, {
    provider: new ReCaptchaV3Provider(siteKey),
    isTokenAutoRefreshEnabled: true,
  });
}

export const auth = getAuth(app);
export const db = getFirestore(app);

/** §16 — pre-generated TTS audio and lesson diagrams. Read-only for clients,
 *  and only inside their own org's prefix (storage.rules). */
export const storage = getStorage(app);

/**
 * §22.2 — no browser storage. The default Firebase Auth persistence writes the
 * session to localStorage, so it is replaced with in-memory persistence: the
 * session lives for the tab and nothing about a rep survives on the device.
 * The visible consequence is that a page refresh returns to sign-in.
 */
export const authReady = setPersistence(auth, inMemoryPersistence);

if (import.meta.env.VITE_USE_EMULATORS === 'true') {
  connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true });
  connectFirestoreEmulator(db, '127.0.0.1', 8080);
  connectStorageEmulator(storage, '127.0.0.1', 9199);
}
